import { defineComponent as _defineComponent } from 'vue';
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, toDisplayString as _toDisplayString } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "widget-header"
};
const _hoisted_2 = { class: "header-content" };
const _hoisted_3 = { class: "header-actions" };
const _hoisted_4 = ["title", "aria-label", "aria-expanded"];
const _hoisted_5 = {
    key: 0,
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    "aria-hidden": "true"
};
const _hoisted_6 = {
    key: 1,
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 24 24",
    stroke: "currentColor",
    "aria-hidden": "true"
};
const _hoisted_7 = ["disabled", "title", "aria-label"];
const _hoisted_8 = {
    key: 1,
    class: "widget-loading"
};
const _hoisted_9 = { class: "loading-text" };
const _hoisted_10 = {
    key: 2,
    class: "widget-error"
};
const _hoisted_11 = { class: "error-message" };
const _hoisted_12 = ["id", "data-embed-id"];
import { ref, onMounted, onUnmounted, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { ChartUseCases } from "@/application/use-cases/ChartUseCases";
import Loader from "@/presentation/components/Loader.vue";
import { useLogger } from "@/shared/composables/useLogger";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ToucanWidget',
    props: {
        chart: {},
        hideActions: { type: Boolean }
    },
    setup(__props) {
        const props = __props;
        const logger = useLogger();
        const { locale } = useI18n();
        const chartUseCases = new ChartUseCases();
        const isLoading = ref(true);
        const hasError = ref(false);
        const isExpanded = ref(false);
        // Generate unique container ID for each widget
        const containerId = computed(() => `toucan-widget-${props.chart.id}`);
        // Helper to normalize language for Toucan
        function getToucanLang(currentLocale) {
            const lang = currentLocale.split("-")[0].toLowerCase();
            if (["fr", "en", "nl"].includes(lang)) {
                return lang;
            }
            return "fr";
        }
        async function loadWidget(forceReload = false) {
            isLoading.value = true;
            hasError.value = false;
            try {
                const lang = getToucanLang(locale.value);
                // Load Toucan script for this specific embedId
                await chartUseCases.initializeToucanWidget(`toucan-widget-${props.chart.id}`, props.chart.embedId, props.chart.token, lang, forceReload);
                // Toucan script is loaded, widget should start rendering
                isLoading.value = false;
            }
            catch (error) {
                logger.debug(`Error loading Toucan widget ${props.chart.id}:`, error);
                hasError.value = true;
                isLoading.value = false;
            }
        }
        // Watch for language changes
        watch(locale, (newLocale) => {
            logger.debug(`Language changed to ${newLocale}, reloading Toucan widget ${props.chart.id}`);
            // We need to force reload to update the iframe/script with new language
            reloadWidget();
        });
        function reloadWidget() {
            logger.debug(`Reloading Toucan widget: ${props.chart.id}`);
            // Clear container content to remove old widget
            const container = document.getElementById(containerId.value);
            if (container) {
                container.innerHTML = "";
            }
            loadWidget(true);
        }
        function toggleExpand() {
            isExpanded.value = !isExpanded.value;
        }
        // Handle Escape key to close fullscreen
        function handleKeydown(e) {
            if (e.key === "Escape" && isExpanded.value) {
                isExpanded.value = false;
            }
        }
        onMounted(async () => {
            window.addEventListener("keydown", handleKeydown);
            await loadWidget();
        });
        onUnmounted(() => {
            window.removeEventListener("keydown", handleKeydown);
            logger.debug(`Unmounting Toucan widget: ${props.chart.id}`);
            // Cleanup is handled globally by Charts.vue
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["toucan-widget", { loading: isLoading.value, error: hasError.value, expanded: isExpanded.value }])
            }, [
                (!__props.hideActions)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createElementVNode("div", _hoisted_2, [
                            _createElementVNode("div", _hoisted_3, [
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "action-button",
                                    title: isExpanded.value ? _ctx.$t('dashboard.charts.collapse') : _ctx.$t('dashboard.charts.expand'),
                                    "aria-label": isExpanded.value ? _ctx.$t('dashboard.charts.collapse') : _ctx.$t('dashboard.charts.expand'),
                                    "aria-expanded": isExpanded.value,
                                    onClick: toggleExpand
                                }, [
                                    (isExpanded.value)
                                        ? (_openBlock(), _createElementBlock("svg", _hoisted_5, [...(_cache[0] || (_cache[0] = [
                                                _createElementVNode("path", {
                                                    "stroke-linecap": "round",
                                                    "stroke-linejoin": "round",
                                                    "stroke-width": "2",
                                                    d: "M6 18L18 6M6 6l12 12"
                                                }, null, -1 /* CACHED */)
                                            ]))]))
                                        : (_openBlock(), _createElementBlock("svg", _hoisted_6, [...(_cache[1] || (_cache[1] = [
                                                _createElementVNode("path", {
                                                    "stroke-linecap": "round",
                                                    "stroke-linejoin": "round",
                                                    "stroke-width": "2",
                                                    d: "M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                                }, null, -1 /* CACHED */)
                                            ]))]))
                                ], 8 /* PROPS */, _hoisted_4),
                                _createElementVNode("button", {
                                    type: "button",
                                    class: "action-button",
                                    disabled: isLoading.value,
                                    title: _ctx.$t('dashboard.charts.refresh'),
                                    "aria-label": _ctx.$t('dashboard.charts.refresh'),
                                    onClick: reloadWidget
                                }, [
                                    (_openBlock(), _createElementBlock("svg", {
                                        xmlns: "http://www.w3.org/2000/svg",
                                        fill: "none",
                                        viewBox: "0 0 24 24",
                                        stroke: "currentColor",
                                        "aria-hidden": "true",
                                        class: _normalizeClass({ spinning: isLoading.value })
                                    }, [...(_cache[2] || (_cache[2] = [
                                            _createElementVNode("path", {
                                                "stroke-linecap": "round",
                                                "stroke-linejoin": "round",
                                                "stroke-width": "2",
                                                d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            }, null, -1 /* CACHED */)
                                        ]))], 2 /* CLASS */))
                                ], 8 /* PROPS */, _hoisted_7)
                            ])
                        ])
                    ]))
                    : _createCommentVNode("v-if", true),
                (isLoading.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                        _createVNode(Loader),
                        _createElementVNode("span", _hoisted_9, _toDisplayString(_ctx.$t("dashboard.charts.loading")), 1 /* TEXT */)
                    ]))
                    : _createCommentVNode("v-if", true),
                (hasError.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                        _cache[3] || (_cache[3] = _createElementVNode("svg", {
                            class: "error-icon",
                            xmlns: "http://www.w3.org/2000/svg",
                            fill: "none",
                            viewBox: "0 0 24 24",
                            stroke: "currentColor",
                            "aria-hidden": "true"
                        }, [
                            _createElementVNode("path", {
                                "stroke-linecap": "round",
                                "stroke-linejoin": "round",
                                "stroke-width": "2",
                                d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            })
                        ], -1 /* CACHED */)),
                        _createElementVNode("p", _hoisted_11, _toDisplayString(_ctx.$t("dashboard.charts.loadError")), 1 /* TEXT */)
                    ]))
                    : _createCommentVNode("v-if", true),
                _createElementVNode("div", {
                    id: containerId.value,
                    class: "toucan-container",
                    "data-embed-id": __props.chart.embedId
                }, null, 8 /* PROPS */, _hoisted_12)
            ], 2 /* CLASS */));
        };
    }
});
